import { useState } from 'react'
import { Radio, Video, Link, ChevronUp, ChevronDown, Archive, AlertCircle, Clock, HardDrive } from 'lucide-react'

function isLiveRecording(video) {
  return !!(video?.liveInput || video?.input?.uid || video?.live_input)
}

export default function StreamTable({
  videos,
  loading,
  selected,
  onSelect,
  checked,
  onToggle,
  onToggleAll,
  vodLinks,
  archiveStatuses = {},
  downloadStatuses = {},
}) {
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  function handleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else if (sortDir === 'desc') { setSortKey(null); setSortDir('asc') }
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  function sortIcon(key) {
    if (sortKey !== key) return null
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
  }

  const sorted = [...videos]
  if (sortKey) {
    sorted.sort((a, b) => {
      let av, bv
      switch (sortKey) {
        case 'title':
          av = (a.meta?.name || '').toLowerCase()
          bv = (b.meta?.name || '').toLowerCase()
          break
        case 'type':
          av = isLiveRecording(a) ? 'live' : 'vod'
          bv = isLiveRecording(b) ? 'live' : 'vod'
          break
        case 'duration':
          av = a.duration || 0
          bv = b.duration || 0
          break
        case 'created':
          av = a.created || ''
          bv = b.created || ''
          break
        case 'archive':
          av = archiveStatuses[a.uid]?.status || 'none'
          bv = archiveStatuses[b.uid]?.status || 'none'
          break
        case 'status':
          av = a.readyToStream ? 'ready' : 'processing'
          bv = b.readyToStream ? 'ready' : 'processing'
          break
        default:
          return 0
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }

  const allChecked = sorted.length > 0 && sorted.every(v => checked.has(v.uid))

  function formatDuration(secs) {
    if (!secs) return '—'
    const s = Math.round(secs)
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  function formatDate(d) {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  function formatSize(bytes) {
    if (!bytes) return ''
    if (bytes > 1e9) return `${(bytes / 1e9).toFixed(1)} GB`
    if (bytes > 1e6) return `${(bytes / 1e6).toFixed(1)} MB`
    return `${(bytes / 1e3).toFixed(0)} KB`
  }

  function archiveBadge(uid) {
    const info = archiveStatuses[uid]
    if (!info || info.status === 'none') return <span className="archive-badge archive-none">—</span>
    switch (info.status) {
      case 'done':
        return <span className="archive-badge archive-done" title={info.filename}><Archive size={10} /> {formatSize(info.size)}</span>
      case 'error':
        return <span className="archive-badge archive-error" title={info.message}><AlertCircle size={10} /> Failed</span>
      case 'not_found':
        return <span className="archive-badge archive-missing" title={info.message}><AlertCircle size={10} /> Missing</span>
      case 'preparing':
      case 'queued':
      case 'transferring':
        return <span className="archive-badge archive-progress"><Clock size={10} /> {info.progress ? `${info.progress}%` : 'In Progress'}</span>
      default:
        return <span className="archive-badge archive-none">—</span>
    }
  }

  return (
    <div className="stream-table-wrap">
      <table className="stream-table">
        <thead>
          <tr>
            <th className="th-check">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={() => onToggleAll(sorted)}
              />
            </th>
            <th className="th-thumb">Thumb</th>
            <th className="th-title sortable" onClick={() => handleSort('title')}>
              Title {sortIcon('title')}
            </th>
            <th className="th-type sortable" onClick={() => handleSort('type')}>
              Type {sortIcon('type')}
            </th>
            <th className="th-status sortable" onClick={() => handleSort('status')}>
              Status {sortIcon('status')}
            </th>
            <th className="th-mp4">MP4</th>
            <th className="th-vod">VOD</th>
            <th className="th-archive sortable" onClick={() => handleSort('archive')}>
              Archive {sortIcon('archive')}
            </th>
            <th className="th-duration sortable" onClick={() => handleSort('duration')}>
              Duration {sortIcon('duration')}
            </th>
            <th className="th-created sortable" onClick={() => handleSort('created')}>
              Created {sortIcon('created')}
            </th>
            <th className="th-uid">UID</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={11} style={{ textAlign: 'center', padding: 30 }}>Loading...</td></tr>
          ) : sorted.length === 0 ? (
            <tr><td colSpan={11} style={{ textAlign: 'center', padding: 30, color: '#666' }}>No videos found</td></tr>
          ) : sorted.map(video => {
            const isActive = selected?.uid === video.uid
            const isChecked = checked.has(video.uid)
            const vod = vodLinks?.[video.uid]
            const thumb = video.thumbnail || (video.uid ? `https://customer-${video.uid?.substring(0, 8)}.cloudflarestream.com/${video.uid}/thumbnails/thumbnail.jpg?time=5s&width=120` : null)

            return (
              <tr
                key={video.uid}
                className={`${isActive ? 'active' : ''} ${isChecked ? 'checked' : ''}`}
                onClick={() => onSelect(video.uid)}
              >
                <td className="td-check" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(video.uid)}
                  />
                </td>
                <td className="td-thumb">
                  {thumb ? (
                    <img src={thumb} alt="" className="stream-thumb" onError={e => e.target.style.display = 'none'} />
                  ) : (
                    <div className="stream-thumb-placeholder" />
                  )}
                </td>
                <td className="td-title">{video.meta?.name || '(untitled)'}</td>
                <td className="td-type">
                  {isLiveRecording(video) ? (
                    <span className="badge-live"><Radio size={10} /> LIVE</span>
                  ) : (
                    <span className="badge-upload"><Video size={10} /> VOD</span>
                  )}
                </td>
                <td className="td-status">
                  {video.readyToStream ? (
                    <span className="stream-status-ready">Ready</span>
                  ) : (
                    <span className="stream-status-processing">Processing</span>
                  )}
                </td>
                <td className="td-mp4">
                  {(() => {
                    const dl = downloadStatuses[video.uid]
                    if (!dl || dl.status === 'none' || dl.status === 'unknown') return <span className="mp4-badge none">—</span>
                    if (dl.status === 'ready') return <span className="mp4-badge ready">Ready</span>
                    if (dl.status === 'inprogress') return <span className="mp4-badge progress">{dl.pctComplete != null ? `${dl.pctComplete}%` : '...'}</span>
                    if (dl.status === 'queued' || dl.status === 'preparing') return <span className="mp4-badge progress">Queued</span>
                    if (dl.status === 'error') return <span className="mp4-badge error">Error</span>
                    return <span className="mp4-badge none">—</span>
                  })()}
                </td>
                <td className="td-vod">
                  {vod?.linked ? (
                    <span className="vod-linked-badge"><Link size={10} /> Linked</span>
                  ) : '—'}
                </td>
                <td className="td-archive">{archiveBadge(video.uid)}</td>
                <td className="td-duration">{formatDuration(video.duration)}</td>
                <td className="td-created">{formatDate(video.created)}</td>
                <td className="td-uid">{video.uid?.substring(0, 8)}...</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
